/**
 * Reine Auswahl-Logik der Branchen-Mehrfachauswahl — ohne React/DOM, damit sie
 * per Vitest prüfbar ist (wie `verantwortlichkeitDropdownLogik.ts`).
 *
 * Fachliche Form (PO 15.08.2026): „Gruppe → mehrere Spezialisierungen und
 * mehrere Gruppen → mehrere Spezialisierungen". Gespeichert wird ausschließlich
 * auf Ebene 3 — je gewähltem Blatt eine Zeile in `ContactBrancheAssignment`.
 *
 * WARUM die id und nie das Label: Ebene-3-Namen sind nicht eindeutig.
 * „Netzbetreiber" gibt es unter Energie & Umwelt/Energieversorgung UND unter
 * IT & Digitalwirtschaft/Telekommunikation, „Anlagenbau" unter
 * Energie & Umwelt/Umwelttechnik UND unter Industrie & Produktion/Maschinen- &
 * Anlagenbau, und „Sonstige" seit 08/2026 in jeder Branche (Katalog: 220
 * Blätter).
 */

import type { BrancheLeaf } from "../../../data/brancheClassification";
import { findLeaf, formatBranchePath, waehlbareLeaves } from "../../../data/brancheClassification";
import { norm } from "../../verantwortlichkeit/verantwortlichkeitDropdownLogik";

/** Eine gewählte Branche = ein Katalog-Blatt + optionale Tätigkeitsbeschreibung. */
export interface BrancheAuswahl {
  brancheClassId: number;
  taetigkeitsbeschreibung?: string | null;
}

/** Eine wählbare Zeile im Panel. */
export interface BrancheOption {
  /** Katalog-id als String (Panel/Chips arbeiten mit Strings). */
  value: string;
  /** Ebene 3 — die Spezialisierung. */
  label: string;
  /** Abschnittsüberschrift: Ebene 2, ohne gesetzte Ebene 1 „E1 / E2". */
  bereich: string;
  /** Erläuterung aus dem Katalog — das „ⓘ" der Zeile. */
  erlaeuterung?: string | null;
  /**
   * Bestandswert aus einem deaktivierten Katalog-Eintrag. Steht nur in der
   * Liste, weil diese Firma ihn schon trägt — neu wählbar ist er nicht mehr.
   */
  deaktiviert?: boolean;
}

/** Ein Abschnitt des Panels (eine Branchengruppe). */
export interface BrancheOptionGruppe {
  bereich: string;
  options: BrancheOption[];
}

/**
 * Wählbare Blätter. Mit gesetzter Ebene 1 nur deren Blätter (9–17 Stück,
 * Überschrift = Ebene 2); ohne Ebene 1 der ganze Katalog, damit man eine
 * Spezialisierung auch dann findet, wenn man ihre Oberbranche nicht kennt
 * („Netzbetreiber" ohne zu wissen, ob Energie oder Telekommunikation).
 *
 * `gewaehlt` sind die ids der schon erfassten Blätter: deaktivierte Einträge
 * fallen aus der Liste, ein bereits GESPEICHERTER bleibt aber drin (als
 * `deaktiviert` gekennzeichnet). Sonst verschwände er beim nächsten Speichern
 * still von der Firma — sie stünde ohne Branche da, ohne dass jemand sie
 * geändert hätte.
 */
export function brancheOptionen(
  rows: BrancheLeaf[],
  ebene1: string,
  gewaehlt: Iterable<number> = [],
): BrancheOption[] {
  return waehlbareLeaves(rows, gewaehlt)
    .filter((l) => !ebene1 || l.ebene1 === ebene1)
    .map((l) => ({
      value: String(l.id),
      label: l.ebene3,
      bereich: ebene1 ? l.ebene2 : `${l.ebene1} / ${l.ebene2}`,
      erlaeuterung: l.erlaeuterung ?? null,
      deaktiviert: l.active === false,
    }));
}

/**
 * Sucht über Spezialisierung, Gruppe UND Erläuterung — wer „Photovoltaik"
 * tippt, findet auch das Blatt, das den Begriff nur im Erklärtext trägt.
 * Mehrwortsuche mit UND-Verknüpfung, Reihenfolge egal.
 */
export function optionenFiltern(options: BrancheOption[], query: string): BrancheOption[] {
  const q = norm(query.trim());
  if (!q) return options;
  const teile = q.split(/\s+/).filter(Boolean);
  return options.filter((o) => {
    const heu = `${norm(o.label)} ${norm(o.bereich)} ${norm(o.erlaeuterung ?? "")}`;
    return teile.every((t) => heu.includes(t));
  });
}

/** Gruppiert nach `bereich`; Reihenfolge folgt dem ersten Auftreten (= Katalog). */
export function optionenGruppieren(options: BrancheOption[]): BrancheOptionGruppe[] {
  const nach = new Map<string, BrancheOption[]>();
  for (const o of options) {
    const liste = nach.get(o.bereich);
    if (liste) liste.push(o);
    else nach.set(o.bereich, [o]);
  }
  return Array.from(nach, ([bereich, opts]) => ({ bereich, options: opts }));
}

/** Flache Liste in Anzeige-Reihenfolge — Grundlage der Tastatur-Navigation. */
export function optionenFlach(gruppen: BrancheOptionGruppe[]): BrancheOption[] {
  return gruppen.flatMap((g) => g.options);
}

/**
 * Verwirft ungültige ids und Dubletten. Der erste Treffer gewinnt, damit eine
 * bereits erfasste Tätigkeitsbeschreibung nicht von einer leeren Zweitzeile
 * überschrieben wird.
 */
export function auswahlNormalisieren(auswahl: BrancheAuswahl[]): BrancheAuswahl[] {
  const gesehen = new Set<number>();
  const out: BrancheAuswahl[] = [];
  for (const a of auswahl) {
    const id = Number(a?.brancheClassId);
    if (!Number.isFinite(id) || id <= 0 || gesehen.has(id)) continue;
    gesehen.add(id);
    out.push({ brancheClassId: id, taetigkeitsbeschreibung: a.taetigkeitsbeschreibung ?? null });
  }
  return out;
}

/**
 * Eine Spezialisierung an-/abwählen. Neue landen am Ende — die Reihenfolge ist
 * fachlich relevant: die ERSTE Zeile ist die Hauptbranche.
 */
export function auswahlUmschalten(auswahl: BrancheAuswahl[], id: number): BrancheAuswahl[] {
  const drin = auswahl.some((a) => a.brancheClassId === id);
  return drin
    ? auswahl.filter((a) => a.brancheClassId !== id)
    : [...auswahl, { brancheClassId: id, taetigkeitsbeschreibung: null }];
}

/**
 * Ganze Branchengruppe an-/abwählen: sind noch nicht alle Blätter der Gruppe
 * gewählt, kommen die fehlenden dazu; sind alle gewählt, fliegen alle raus.
 *
 * Das ist der Ersatz für „Gruppe ohne Spezialisierung": `ebene3` ist NOT NULL
 * und Teil des Unique-Index, eine Gruppe allein ist nicht speicherbar. Ein Klick
 * auf die Gruppe wählt deshalb ihre Blätter — fachlich „die ganze Gruppe",
 * technisch n Zeilen, ohne Schemaänderung.
 */
export function gruppeUmschalten(auswahl: BrancheAuswahl[], ids: number[]): BrancheAuswahl[] {
  if (ids.length === 0) return auswahl;
  const vorhanden = new Set(auswahl.map((a) => a.brancheClassId));
  const alleDrin = ids.every((id) => vorhanden.has(id));
  if (alleDrin) return auswahl.filter((a) => !ids.includes(a.brancheClassId));
  const fehlend = ids.filter((id) => !vorhanden.has(id));
  return [...auswahl, ...fehlend.map((id) => ({ brancheClassId: id, taetigkeitsbeschreibung: null }))];
}

/** Eine Spezialisierung entfernen (samt ihrer Tätigkeitsbeschreibung). */
export function auswahlEntfernen(auswahl: BrancheAuswahl[], id: number): BrancheAuswahl[] {
  return auswahl.filter((a) => a.brancheClassId !== id);
}

// `beschreibungSetzen` ist entfallen (PO 17.08.2026): Die Tätigkeitsbeschreibung
// gilt dem ganzen Betrieb und steht als „Tätigkeitsbeschreibung" am Kontakt,
// nicht je Branchen-Zeile. Das Feld bleibt im Typ und in der Signatur, weil die
// Datenbank die Texte des Bestands weiterführt — sie sind nur über diese Maske
// nicht mehr pflegbar.

/**
 * Eine gewählte Zeile zur Hauptbranche machen — sie wandert an Position 0.
 * Die Hauptbranche steht zusätzlich als `Contact.brancheClassId` am Kontakt und
 * ist das, was der Risiken-Reiter je Firma anzeigt.
 */
export function alsHauptbrancheSetzen(auswahl: BrancheAuswahl[], id: number): BrancheAuswahl[] {
  const treffer = auswahl.find((a) => a.brancheClassId === id);
  if (!treffer) return auswahl;
  return [treffer, ...auswahl.filter((a) => a.brancheClassId !== id)];
}

/** Die Hauptbranche = erste Zeile der Auswahl. */
export function hauptbranche(auswahl: BrancheAuswahl[]): BrancheAuswahl | undefined {
  return auswahl[0];
}

/** Anzeigepfad der Hauptbranche („E1 / E2 / E3") oder "". */
export function hauptbranchePfad(rows: BrancheLeaf[], auswahl: BrancheAuswahl[]): string {
  const h = hauptbranche(auswahl);
  if (!h) return "";
  return formatBranchePath(rows.find((r) => r.id === h.brancheClassId));
}

/** Vergleichsform für das Dirty-Tracking — Reihenfolge zählt (Hauptbranche!). */
export function auswahlSignatur(auswahl: BrancheAuswahl[]): string {
  return JSON.stringify(
    auswahl.map((a) => [a.brancheClassId, (a.taetigkeitsbeschreibung ?? "").trim()]),
  );
}

// --- Blöcke der Maske ------------------------------------------------------
//
// Gespeichert wird weiterhin die flache, geordnete Liste der Ebene-3-Blätter
// (oben). Die Maske bündelt sie seit 08/2026 je Ebene 1 zu Blöcken: „mehrere
// Branchen, je Branche mehrere Gruppen bzw. Spezialisierungen" (PO). Ebene 1 ist
// damit nicht mehr bloßer Anzeigefilter, sondern der Schlüssel des Blocks.

/** Ein Eingabe-Block: EINE Branche (Ebene 1) mit ihren gewählten Blättern. */
export interface BrancheBlock {
  ebene1: string;
  ids: number[];
}

/**
 * Flache Auswahl → Blöcke, gruppiert nach Ebene 1 in Reihenfolge des ersten
 * Auftretens. Blätter derselben Branche rücken dabei zusammen; die Hauptbranche
 * bleibt das erste Blatt des ersten Blocks.
 *
 * Unbekannte ids (Katalog noch nicht geladen, Blatt gelöscht) sammeln sich in
 * einem Block ohne Ebene 1, statt still wegzufallen. Ohne Auswahl bleibt EIN
 * leerer Block stehen — die Maske zeigt immer mindestens eine Branche.
 */
export function bloeckeAusAuswahl(rows: BrancheLeaf[], auswahl: BrancheAuswahl[]): BrancheBlock[] {
  const bloecke: BrancheBlock[] = [];
  for (const a of auswahlNormalisieren(auswahl)) {
    const ebene1 = findLeaf(rows, a.brancheClassId)?.ebene1 ?? "";
    const treffer = bloecke.find((b) => b.ebene1 === ebene1);
    if (treffer) treffer.ids.push(a.brancheClassId);
    else bloecke.push({ ebene1, ids: [a.brancheClassId] });
  }
  return bloecke.length > 0 ? bloecke : [{ ebene1: "", ids: [] }];
}

/**
 * Blöcke → flache Auswahl in Blockreihenfolge. Blöcke ohne Blatt fallen weg:
 * eine Branche ohne Spezialisierung ist nicht speicherbar (`ebene3` NOT NULL).
 *
 * `vorher` reicht die Tätigkeitsbeschreibungen des Bestands nach — sie sind über
 * diese Maske nicht mehr pflegbar, dürfen beim Speichern aber nicht verloren
 * gehen.
 */
export function auswahlAusBloecken(
  bloecke: BrancheBlock[],
  vorher: BrancheAuswahl[] = [],
): BrancheAuswahl[] {
  const texte = new Map(vorher.map((a) => [a.brancheClassId, a.taetigkeitsbeschreibung ?? null]));
  return auswahlNormalisieren(
    bloecke.flatMap((b) =>
      b.ids.map((id) => ({ brancheClassId: id, taetigkeitsbeschreibung: texte.get(id) ?? null })),
    ),
  );
}

const alsIds = (auswahl: BrancheAuswahl[]): number[] => auswahl.map((a) => a.brancheClassId);
const alsAuswahl = (ids: number[]): BrancheAuswahl[] => ids.map((id) => ({ brancheClassId: id }));

/** `auswahlUmschalten` auf den ids eines Blocks. */
export function idsUmschalten(ids: number[], id: number): number[] {
  return alsIds(auswahlUmschalten(alsAuswahl(ids), id));
}

/** `gruppeUmschalten` auf den ids eines Blocks. */
export function idsGruppeUmschalten(ids: number[], gruppenIds: number[]): number[] {
  return alsIds(gruppeUmschalten(alsAuswahl(ids), gruppenIds));
}

/**
 * Ein Blatt zur Hauptbranche machen: sein Block wandert an den Anfang der Liste,
 * es selbst an den Anfang des Blocks. Die Hauptbranche ist damit weiterhin das
 * erste Blatt der flachen Auswahl — sichtbar als erste Zeile des ersten Blocks.
 */
export function hauptbrancheInBloecken(bloecke: BrancheBlock[], id: number): BrancheBlock[] {
  const i = bloecke.findIndex((b) => b.ids.includes(id));
  if (i < 0) return bloecke;
  const ziel: BrancheBlock = { ...bloecke[i], ids: alsIds(alsHauptbrancheSetzen(alsAuswahl(bloecke[i].ids), id)) };
  return [ziel, ...bloecke.filter((_, j) => j !== i)];
}
